import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useCountDown } from 'ahooks'
import { ToolComponentCtx } from '@hose/chat-app'
import { isMatchedDataHidden, setMatchedDataHidden } from '../utils/storage'

export interface UseMatchedDataOptions<T> {
  /**
   * 默认数据
   */
  defaultData: T[]
  /**
   * 是否启用倒计时自动提交
   */
  enableAutoSubmit?: boolean
  /**
   * 倒计时时间（秒），默认 30 秒
   */
  countdownSeconds?: number
  /**
   * 提交后是否自动隐藏组件
   */
  hideAfterSubmit?: boolean
  /**
   * 是否启用缓存功能，确保重新加载时保持隐藏状态
   */
  enableCache?: boolean
  /**
   * 数据解析函数，用于自定义解析逻辑
   */
  parseData?: (result: any) => T[]
}

export interface UseMatchedDataReturn<T> {
  /**
   * 当前匹配的数据
   */
  matchedData: T[]
  /**
   * 设置匹配数据
   */
  setMatchedData: React.Dispatch<React.SetStateAction<T[]>>
  /**
   * AI 解析的结果数据
   */
  AIResultDatas: T[]
  /**
   * 重置数据到初始状态
   */
  onReset: () => void
  /**
   * 确认提交数据
   */
  onConfirm: () => void
  /**
   * 是否已提交
   */
  isSubmitted: boolean
  /**
   * 是否隐藏组件
   */
  isHidden: boolean
  /**
   * 倒计时剩余时间（秒）
   */
  countdown: number
  /**
   * 是否正在倒计时
   */
  isCountdown: boolean
  /**
   * 取消倒计时（保留当前秒数，不再自动提交）
   */
  cancelCountdown: () => void
}

/**
 * 通用的匹配数据管理 Hook
 * 提供数据解析、重置、确认、倒计时自动提交等功能
 */
export function useMatchedData<T>(
  props: ToolComponentCtx,
  options: UseMatchedDataOptions<T>
): UseMatchedDataReturn<T> {
  const {
    defaultData,
    enableAutoSubmit = false,
    countdownSeconds = 10,
    hideAfterSubmit = true,
    enableCache = true,
    parseData
  } = options

  const { state, toolCallId, sendToolResult } = props

  // 状态管理
  const [matchedData, setMatchedData] = useState<T[]>(defaultData)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isHidden, setIsHidden] = useState(() => enableCache ? isMatchedDataHidden(toolCallId) : false)
  const [countdown, setCountdown] = useState(countdownSeconds)
  const [targetDate, setTargetDate] = useState<number | undefined>(undefined)
  const [msLeft] = useCountDown({
    targetDate,
    onEnd: () => {
      setTargetDate(undefined)
      onConfirm()
    },
  })
  const isCountdown = !!targetDate && msLeft > 0
  const cancelCountdown = useCallback(() => {
    setTargetDate(undefined)
  }, [])

  // 解析 AI 结果数据
  const AIResultDatas: T[] = useMemo(() => {
    if (props.state === 'result') {
      if (typeof props?.result === 'string') {
        try {
          const parsed = JSON.parse(props?.result)
          return parseData ? parseData(parsed) : parsed
        } catch {
          return parseData ? parseData(props?.result) : props?.result
        }
      } else {
        const result = props?.result || []
        return parseData ? parseData(result) : result
      }
    }
    return []
  }, [props, parseData])

  // 重置数据
  const onReset = useCallback(() => {
    if (Array.isArray(AIResultDatas) && AIResultDatas?.length > 0) {
      setMatchedData(AIResultDatas)
    } else {
      setMatchedData(defaultData)
    }
    setIsSubmitted(false)
    setIsHidden(false)
    setTargetDate(undefined)
    setCountdown(countdownSeconds)
  }, [AIResultDatas, defaultData, countdownSeconds])

  // 确认提交
  const onConfirm = useCallback(() => {
    console.log('onConfirm', props, {
      toolCallId,
      result: matchedData
    })
    try {
      sendToolResult({
        toolCallId,
        result: matchedData
      })
      setIsSubmitted(true)
    } catch (error) {
      
    }
    
    
    if (hideAfterSubmit) {
      // 如果启用缓存，保存隐藏状态到缓存
      if (enableCache) {
        setMatchedDataHidden(toolCallId)
      }
      setIsHidden(true)
    }
  }, [toolCallId, matchedData, hideAfterSubmit, enableCache, props, sendToolResult])

  // 初始化数据：仅在首次有结果时设置一次，避免覆盖用户操作
  const hasInitializedRef = useRef(false)
  useEffect(() => {
    if (hasInitializedRef.current) return
    if (state !== 'result') return
    // 仅在首次进入 result 且拿到数据时初始化
    hasInitializedRef.current = true
    if (Array.isArray(AIResultDatas) && AIResultDatas.length > 0) {
      setMatchedData(AIResultDatas)
    } else {
      setMatchedData(defaultData)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, AIResultDatas])

  // 驱动 targetDate 与展示秒数
  useEffect(() => {
    if (!enableAutoSubmit || state !== 'result' || isSubmitted || isHidden) {
      // 条件不满足则停止
      setTargetDate(undefined)
      return
    }
    setTargetDate(Date.now() + countdownSeconds * 1000)
  }, [enableAutoSubmit, state, isSubmitted, isHidden, countdownSeconds])

  // 将毫秒映射为秒显示，避免显示 0
  useEffect(() => {
    if (!targetDate) {
      setCountdown(countdownSeconds)
      return
    }
    const sec = Math.ceil(msLeft / 1000)
    setCountdown(sec > 0 ? sec : 1)
  }, [msLeft, targetDate, countdownSeconds])

  // 如果组件被隐藏，返回空内容
  if (isHidden) {
    return {
      matchedData: [],
      setMatchedData,
      AIResultDatas: [],
      onReset,
      onConfirm,
      isSubmitted: true,
      isHidden: true,
      countdown: 0,
      isCountdown: false,
      cancelCountdown
    }
  }

  return {
    matchedData,
    setMatchedData,
    AIResultDatas,
    onReset,
    onConfirm,
    isSubmitted,
    isHidden,
    countdown,
    isCountdown,
    cancelCountdown
  }
}
